import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Badge, Collapse, Popconfirm, Spin, Tag, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { isStaffEmail } from "@utils/user_utils";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import { AdminNotice } from "@components/Settings/AdminNotice";
import { PermissionedButton as PermissionedButtonBase, useUserPermissions } from "@components/PermissionedComponents";
const PermissionedButton = PermissionedButtonBase as unknown as React.FC<any>;
import {
    createUserDefinedMetric,
    deleteUserDefinedMetric,
    getUserDefinedMetrics,
    updateUserDefinedMetric,
} from "@store/actions/team.actions";
import { IUserDefinedMetric, METRIC_TYPE_OPTIONS } from "./constants";
import MetricForm from "./MetricForm";
import CopyMetricButton from "./CopyMetricButton";
import "@styles/styles-UserDefinedMetrics.css";
import BlurUDM from "./blurred_user_defined_metrics.png";

const { Text } = Typography;

const blankMetric: IUserDefinedMetric = {
    name: "",
    metric_type: "gps",
    config: { formula: "", fetch_params: {} },
    unit: null,
    decimal_places: 2,
    upper: null,
    lower: null,
    color_strategy: null,
    is_active: true,
    is_visible: true,
};

interface DraftMetric extends IUserDefinedMetric {
    _tempId: string;
}

export const UserDefinedMetrics: React.FC = () => {
    const dispatch = useDispatch();

    const metrics: IUserDefinedMetric[] = useSelector(
        ({ team }: any) => team.user_defined_metrics
    );

    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);

    const userDefinedMetricsEnabled: boolean = useSelector(
        ({ team }: any) => team.info?.user_defined_metrics_enabled
    ) ?? false;
    const userPermissions = useUserPermissions();
    const canView: boolean = !!userPermissions?.ViewUserDefinedMetrics;
    const permissionsManagerList = useSelector(
        ({ team }: any) => team.info?.permissions_manager_list
    ) ?? [];
    const managerNames = permissionsManagerList
        .map((m: any) => `${m.first_name} ${m.last_name}`)
        .join(", ");

    const [loading, setLoading] = useState<boolean>(true);
    const [creating, setCreating] = useState<boolean>(false);
    const [expandedId, setExpandedId] = useState<number | string | null>(null);
    const [saving, setSaving] = useState<Record<number | string, boolean>>({});
    const [draftMetrics, setDraftMetrics] = useState<DraftMetric[]>([]);

    useEffect(() => {
        if ((!userDefinedMetricsEnabled && !isStaff) || !canView) return;
        const load = async () => {
            setLoading(true);
            await (dispatch as any)(getUserDefinedMetrics());
            setLoading(false);
        };
        load();
    }, [dispatch, userDefinedMetricsEnabled, isStaff, canView]);

    const handleCreate = async (data: IUserDefinedMetric) => {
        setSaving((prev) => ({ ...prev, __new__: true }));
        const ok: boolean = await (dispatch as any)(createUserDefinedMetric(data));
        setSaving((prev) => { const next = { ...prev }; delete next["__new__"]; return next; });
        if (ok) setCreating(false);
    };

    const handleUpdate = async (id: number, data: IUserDefinedMetric) => {
        setSaving((prev) => ({ ...prev, [id]: true }));
        const ok: boolean = await (dispatch as any)(updateUserDefinedMetric(id, data));
        setSaving((prev) => { const next = { ...prev }; delete next[id]; return next; });
        if (ok) setExpandedId(null);
    };

    const handleDelete = async (id: number) => {
        await (dispatch as any)(deleteUserDefinedMetric(id));
        if (expandedId === id) setExpandedId(null);
    };

    const handleDuplicateMetric = (metric: IUserDefinedMetric) => {
        const draft: DraftMetric = {
            ...JSON.parse(JSON.stringify(metric)),
            id: undefined,
            name: `${metric.name} (Copy)`,
            _tempId: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        };
        setDraftMetrics((prev) => [...prev, draft]);
        setExpandedId(draft._tempId);
    };

    const handleSaveDraft = async (key: string, data: IUserDefinedMetric) => {
        setSaving((prev) => ({ ...prev, [key]: true }));
        const ok: boolean = await (dispatch as any)(createUserDefinedMetric(data));
        setSaving((prev) => { const next = { ...prev }; delete next[key]; return next; });
        if (ok) {
            setDraftMetrics((prev) => prev.filter((d) => d._tempId !== key));
            setExpandedId(null);
        }
    };

    const handleCancelDraft = (key: string) => {
        setDraftMetrics((prev) => prev.filter((d) => d._tempId !== key));
        setExpandedId(null);
    };

    const getMetricTypeLabel = (value: string): string =>
        METRIC_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;

    if (!userDefinedMetricsEnabled && !isStaff) {
        return (
            <PremiumFeatureDisabled
                bg_image={BlurUDM}
                show_navbar={false}
                feature_name={"User Defined Metrics"}
                info_text={
                    <>
                        This is a premium feature that lets you build your own calculated values from the data
                        streams that matter most to you. <br />
                        If you are interested in adding this to your platform, please contact your sales rep!
                    </>
                }
            />
        );
    }

    if (!canView && !isStaff) {
        return (
            <div>
                You do not have permissions to view user defined metrics. <br />
                If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br /><br />
                Your team's Permissions Manager(s): <strong>{managerNames}</strong>
            </div>
        );
    }

    return (
        <div className="udm-container">
            {!userDefinedMetricsEnabled && isStaff && <AdminNotice style={{ marginBottom: 16 }} />}
            <div className="udm-header">
                <PermissionedButton
                    permissionCheck="ManageUserDefinedMetrics"
                    permissionMessage="You don't have permission to manage user defined metrics"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setCreating(true); setExpandedId(null); }}
                    disabled={creating}
                >
                    New Metric
                </PermissionedButton>
            </div>

            {creating && (
                <Collapse className="udm-card udm-card-new" activeKey={["__new__"]}>
                    <Collapse.Panel
                        key="__new__"
                        showArrow={false}
                        header={<div className="udm-card-title"><Text strong>New Metric</Text></div>}
                    >
                        <MetricForm
                            initial={blankMetric}
                            saving={!!saving.__new__}
                            onSave={handleCreate}
                            onCancel={() => setCreating(false)}
                        />
                    </Collapse.Panel>
                </Collapse>
            )}

            {loading && metrics.length === 0 && (
                <div className="udm-empty">
                    <Spin />
                </div>
            )}

            {!loading && metrics.length === 0 && draftMetrics.length === 0 && !creating && (
                <div className="udm-empty">
                    No user defined metrics yet. Click <strong>New Metric</strong> to create one.
                </div>
            )}

            {metrics.map((metric) => (
                <Collapse
                    key={metric.id}
                    className="udm-card"
                    activeKey={expandedId === metric.id ? [metric.id!] : []}
                    onChange={(keys) => {
                        if (keys.length > 0) {
                            setExpandedId(metric.id!);
                            setCreating(false);
                        } else {
                            setExpandedId(null);
                        }
                    }}
                >
                    <Collapse.Panel
                        key={metric.id!}
                        header={
                            <div className="udm-card-title">
                                <Text strong>{metric.name}</Text>
                                <Tag className="udm-type-tag">
                                    {getMetricTypeLabel(metric.metric_type)}
                                </Tag>
                                <Tag color={metric.is_active ? "green" : "default"}>
                                    {metric.is_active ? "Active" : "Inactive"}
                                </Tag>
                                {!metric.is_visible && <Tag>Hidden</Tag>}
                                {metric.unit && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {metric.unit}
                                    </Text>
                                )}
                            </div>
                        }
                        extra={
                            <div className="udm-card-actions">
                                <CopyMetricButton
                                    metric={metric}
                                    onDuplicate={handleDuplicateMetric}
                                />
                                <Popconfirm
                                    title="Delete metric"
                                    description={`Are you sure you want to delete "${metric.name}"?`}
                                    onConfirm={(e) => { e?.stopPropagation(); handleDelete(metric.id!); }}
                                    onCancel={(e) => e?.stopPropagation()}
                                    okText="Yes"
                                    okButtonProps={{ danger: true }}
                                    cancelText="No"
                                >
                                    <PermissionedButton
                                        permissionCheck="ManageUserDefinedMetrics"
                                        permissionMessage="You don't have permission to manage user defined metrics"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                    />
                                </Popconfirm>
                            </div>
                        }
                    >
                        <MetricForm
                            initial={metric}
                            isExpanded={expandedId === metric.id}
                            saving={!!saving[metric.id!]}
                            onSave={(data) => handleUpdate(metric.id!, data)}
                            onCancel={() => setExpandedId(null)}
                        />
                    </Collapse.Panel>
                </Collapse>
            ))}

            {draftMetrics.map((draft) => (
                <Collapse
                    key={draft._tempId}
                    className="udm-card udm-card-new"
                    activeKey={[draft._tempId]}
                >
                    <Collapse.Panel
                        key={draft._tempId}
                        showArrow={false}
                        header={
                            <div className="udm-card-title">
                                <Text strong>{draft.name}</Text>
                                <Badge status="processing" text="Unsaved" style={{ marginLeft: 8 }} />
                            </div>
                        }
                    >
                        <MetricForm
                            initial={draft}
                            isExpanded={expandedId === draft._tempId}
                            saving={!!saving[draft._tempId]}
                            onSave={(data) => handleSaveDraft(draft._tempId, data)}
                            onCancel={() => handleCancelDraft(draft._tempId)}
                        />
                    </Collapse.Panel>
                </Collapse>
            ))}
        </div>
    );
};

export default UserDefinedMetrics;
